import React from 'react'

import type {
  CategoryWithChildren,
  CategoryWithParentCategory,
  StoryImage,
  StoryWithKeyIndicatorsCategoriesStoryImageAndResources,
} from '@aqua/db/types'

import { StoryTypes } from '~/enums/enums'
import ParagraphContent from './ParagraphContent'
import StoryKeyIndicatorsTable from './StoryKeyIndicatorsTable'
import StoryResources from './StoryResources'

const StoryHeaderPhoto = ({ storyImage }: { storyImage: StoryImage }) => {
  if (
    !storyImage?.image ||
    !storyImage.photoCredit ||
    !storyImage.description ||
    !storyImage.photoCreditLink
  ) {
    return null
  }

  return (
    <div className='mt-10 text-[14px] leading-5 text-[#718096]'>
      <p>
        Header photo: {storyImage.description} Photo by{' '}
        <span>
          <a
            target='_blank'
            rel='noreferrer'
            className='text-wrap text-primary text-left text-[14px] font-semibold  leading-6 underline'
            href={storyImage.photoCreditLink}
          >
            {storyImage.photoCredit}
          </a>
        </span>
      </p>
    </div>
  )
}

const StorySection = ({
  heading,
  content,
}: {
  heading: string
  content: string
}) => {
  return (
    <div className='mt-8 h-max w-full'>
      <ParagraphContent heading={heading} content={content} />
    </div>
  )
}

const getHeading = (storyType: number) => {
  switch (storyType as StoryTypes) {
    case StoryTypes.Impact:
      return 'Intervention'
    case StoryTypes.Baseline:
      return 'Key Observations'
    default:
      return 'Methods'
  }
}

const StoryContent = ({
  story,
  categoryData,
}: {
  story: {
    storiesData: StoryWithKeyIndicatorsCategoriesStoryImageAndResources
    innermostParents: {
      storyId: number
      type: CategoryWithParentCategory
    }
  }
  categoryData: CategoryWithChildren[]
}) => {
  const { storiesData } = story

  const heading = getHeading(storiesData.storyType)
  return (
    <div className='h-max w-full bg-white px-5 pb-10 pt-6'>
      <StorySection
        heading={heading}
        content={
          storiesData.methInt ? storiesData.methInt : `No ${heading} added`
        }
      />
      <StorySection
        heading='Impact'
        content={storiesData.impact ? storiesData.impact : `No Impact added`}
      />
      {storiesData.keyObservations && (
        <StorySection
          heading='Key Observations'
          content={storiesData.keyObservations ?? ''}
        />
      )}

      <div className='mt-8 h-max w-full overflow-x-scroll '>
        <h2 className='text-[16px] font-bold '>Key Indicators</h2>
        <div className='mt-3 h-max w-full overflow-x-scroll border border-gray-400'>
          <StoryKeyIndicatorsTable categoryData={categoryData} story={story} />
        </div>
      </div>

      {storiesData.description && (
        <StorySection
          heading='Project description'
          content={storiesData.description}
        />
      )}

      {story.storiesData.storyResources &&
        story.storiesData.storyResources.length > 0 && (
          <div className='mt-8 h-max w-full'>
            <StoryResources storiesData={story.storiesData} />
          </div>
        )}

      <StoryHeaderPhoto storyImage={storiesData.storyImage} />
    </div>
  )
}

export default StoryContent
